import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import emailjs from "@emailjs/browser";

import NavBar from "../components/common/navBar";
import Footer from "../components/common/footer";
import BookingCalendar from "../components/booking/bookingCalendar";
import INFO from "../data/user";
import { API_BASE, makeCalendarInviteUrl } from "../data/bookingLinks";

import "../components/booking/booking.css";
import "./styles/bookingReschedule.css";

const EMAILJS_SERVICE_ID = "service_jpezr0s";
const EMAILJS_TEMPLATE_ID = "template_h6tfu65";
const EMAILJS_PUBLIC_KEY = "Qc73pj33bbA0EKVxQ";

function formatBookingDate(date) {
	return new Date(`${date}T00:00:00`).toLocaleDateString("en-ZA", {
		weekday: "long",
		day: "numeric",
		month: "long",
		year: "numeric",
	});
}

function makeRescheduleUrl(token) {
	const url = new URL("/booking/reschedule", window.location.origin);
	url.searchParams.set("token", token);
	return url.toString();
}

async function sendRescheduleEmail(booking, token) {
	const inviteUrl = makeCalendarInviteUrl(booking.bookingId, token);
	const confirmationLinks = [
		`Add this appointment to your calendar: ${inviteUrl}`,
		booking.meetingUrl ? `Join your virtual appointment on Zoom: ${booking.meetingUrl}` : "",
	].filter(Boolean).join("\n");
	await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, {
		to_name: `${booking.name} ${booking.surname}`.trim(),
		to_email: booking.email,
		email_subject: "Booking rescheduled",
		email_title: "Booking Updated",
		email_intro: "Your appointment has been rescheduled. Here are the updated details:",
		report_body: confirmationLinks,
		action_label: "Reschedule again",
		action_url: makeRescheduleUrl(token),
		service_name: booking.serviceTitle,
		mode: booking.mode === "in-person" ? "In Person" : "Virtual",
		booking_date: formatBookingDate(booking.date),
		booking_time: booking.time,
		duration: booking.duration ? `${booking.duration} min` : "",
		calendar_url: inviteUrl,
		meeting_url: booking.meetingUrl || "",
		reschedule_url: makeRescheduleUrl(token),
	}, EMAILJS_PUBLIC_KEY);
}

const BookingReschedule = () => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const [token] = useState(() => searchParams.get("token") || "");
	const [booking, setBooking] = useState(null);
	const [loading, setLoading] = useState(true);
	const [moving, setMoving] = useState(false);
	const [completed, setCompleted] = useState(false);
	const [error, setError] = useState("");
	const [emailError, setEmailError] = useState(false);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	useEffect(() => {
		if (searchParams.has("token")) navigate("/booking/reschedule", { replace: true });
	}, [navigate, searchParams]);

	useEffect(() => {
		if (!token) {
			setError("This reschedule link is missing or invalid. Please use the link from your booking email.");
			setLoading(false);
			return undefined;
		}

		let cancelled = false;
		fetch(`${API_BASE}/notify-booking`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ rescheduleAction: "lookup", token }),
		})
			.then(async (response) => {
				const data = await response.json();
				if (!response.ok) throw new Error(data.message || "Could not load this appointment.");
				if (!cancelled) setBooking(data.booking);
			})
			.catch((requestError) => {
				if (!cancelled) setError(requestError.message || "Could not load this appointment.");
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});

		return () => { cancelled = true; };
	}, [token]);

	const handleReschedule = async (date, time) => {
		setMoving(true);
		setError("");
		try {
			const response = await fetch(`${API_BASE}/notify-booking`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ rescheduleAction: "move", token, date, time }),
			});
			const data = await response.json();
			if (!response.ok) throw new Error(data.message || "Could not reschedule this appointment.");
			setBooking(data.booking);
			setCompleted(true);
			try {
				await sendRescheduleEmail(data.booking, token);
			} catch (emailSendError) {
				console.error("Reschedule confirmation email failed:", emailSendError);
				setEmailError(true);
			}
		} catch (requestError) {
			setError(requestError.message || "Could not reschedule this appointment.");
		} finally {
			setMoving(false);
		}
	};

	const service = booking ? {
		title: booking.serviceTitle,
		duration: booking.duration || 60,
		mode: booking.mode,
	} : null;

	return (
		<>
			<Helmet>
				<title>{`Reschedule Appointment | ${INFO.main.title}`}</title>
				<meta name="robots" content="noindex, nofollow, noarchive" />
				<meta name="referrer" content="no-referrer" />
			</Helmet>
			<div className="page-content">
				<NavBar active="booking" />
				<div className="content-wrapper">
					<main className="booking-reschedule">
						<div className="booking-reschedule__card">
							{loading && (
								<div className="booking-reschedule__state" role="status">
									<span className="booking-reschedule__spinner" aria-hidden="true" />
									<h1>Opening your booking</h1>
									<p>We’re securely checking your appointment details.</p>
								</div>
							)}

							{!loading && error && !completed && (
								<div className="booking-reschedule__state">
									<div className="booking-reschedule__icon" aria-hidden="true">!</div>
									<h1>We couldn’t open this booking</h1>
									<p>{error}</p>
									<a className="booking-reschedule__button booking-reschedule__button--secondary" href="/contact">
										Contact us
									</a>
								</div>
							)}

							{!loading && booking && !completed && (
								<>
									<div className="booking-reschedule__intro">
										<p className="booking-reschedule__eyebrow">Self-service booking</p>
										<h1>Reschedule your appointment</h1>
										<p>Select a new available time. Your change will be confirmed immediately.</p>
									</div>
									<div className="booking-reschedule__current">
										<span>Current appointment</span>
										<strong>{booking.serviceTitle}</strong>
										<p>{formatBookingDate(booking.date)} at {booking.time}</p>
									</div>
									{error && <p className="booking-reschedule__error" role="alert">{error}</p>}
									<div className={moving ? "booking-reschedule__calendar is-moving" : "booking-reschedule__calendar"}>
										<BookingCalendar
											service={service}
											onSelect={handleReschedule}
											onBack={() => navigate("/contact")}
											stepLabel="Reschedule appointment"
											heading="Choose a new date & time"
											description={`Select a new time for your ${booking.serviceTitle} appointment.`}
										/>
										{moving && (
											<div className="booking-reschedule__progress" role="status">
												<span className="booking-reschedule__spinner" aria-hidden="true" />
												<span>Confirming your new appointment…</span>
											</div>
										)}
									</div>
								</>
							)}

							{completed && booking && (
								<div className="booking-reschedule__state">
									<div className="booking-reschedule__icon booking-reschedule__icon--success" aria-hidden="true">✓</div>
									<p className="booking-reschedule__eyebrow">Change confirmed</p>
									<h1>Your appointment has been rescheduled</h1>
									<p><strong>{booking.serviceTitle}</strong></p>
									<p>{formatBookingDate(booking.date)} at {booking.time}</p>
									{emailError && (
										<p className="booking-reschedule__email-warning" role="status">
											Your booking is updated, but we couldn’t send the confirmation email. Please contact us if you need a copy.
										</p>
									)}
									{booking.meetingSetupError && (
										<p className="booking-reschedule__email-warning" role="status">
											Your appointment is updated, but we couldn’t create its Zoom link yet. Please contact us before your appointment.
										</p>
									)}
									<a
										className="booking-reschedule__button"
										href={makeCalendarInviteUrl(booking.bookingId, token)}
									>
										Add to calendar (.ics)
									</a>
									{booking.meetingUrl && (
										<a
											className="booking-reschedule__button"
											href={booking.meetingUrl}
											target="_blank"
											rel="noreferrer"
										>
											Join your Zoom meeting
										</a>
									)}
									<a className="booking-reschedule__button" href="/">Return to the website</a>
								</div>
							)}
						</div>
					</main>
				</div>
				<div className="page-footer">
					<Footer />
				</div>
			</div>
		</>
	);
};

export default BookingReschedule;
